import ShareIcon from '@mui/icons-material/Share';
import { Alert, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import * as React from 'react';
import CopyToClipboard from 'react-copy-to-clipboard';
import { makeStyles } from 'tss-react/mui';
import { API_BASE_URL } from '../../../asyncHelpers';
import { useAiCallSummary } from '../../../sic-lib/api/hooks/useAi';
import { ChannelItemCall } from '../../../sic-lib/types/ChannelItem';
import { setDrawerTranscript } from '../../../store/conversation';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import AxiosErrorAlert from '../../Shared/Alerts/AxiosErrorAlert';
import { DrawerCommon } from '../../Shared/Drawer/DrawerCommon';
import Loader from '../../Shared/Loader';
import PillTabContent from '../../Shared/PillTabContent';
import { PillTab, PillTabs } from '../../Shared/PillTabs';
import DetailsTab from './DetailsTab';
import DevtoolsMenu from './DevtoolsMenu';
import SummaryTab from './SummaryTab';
import TranscriptTab from './TranscriptTab';

type TabOptions = 'Details' | 'Summary' | 'Transcript';

export default function CallDetailsDrawer() {
    const { classes } = useStyles();
    const dispatch = useAppDispatch();

    const [selectedTab, setTabValue] = React.useState<TabOptions>('Summary');
    const [isCopied, setIsCopied] = React.useState<boolean>(false);
    const audioRef = React.useRef<HTMLAudioElement>(null);

    const channelIndexId = useAppSelector(state => state.conversation?.suggestReplyChannelIndexId);
    const channelItem = useAppSelector(
        state => state.conversation?.transcriptDrawerChannelItem as ChannelItemCall,
    );
    const isImpersonated = useAppSelector(state => state.user.isImpersonated);
    const users = useAppSelector(state => state.user.users);

    const showDevTools = React.useMemo(() => {
        return (
            isImpersonated ||
            window.location.hostname === 'localhost' ||
            window.location.hostname === '127.0.0.1'
        );
    }, [isImpersonated]);

    const {
        data: dataSummary,
        isLoading: isLoadingSummary,
        isFetching: isFetchingSummary,
        isError: isErrorSummary,
        error: errorSummary,
        isSuccess: isSuccessSummary,
    } = useAiCallSummary({
        contactId: channelItem?.contactId!,
        channelType: channelItem?.type!,
        channelIndexId: channelItem?.id,
    });

    const handleClose = () => {
        dispatch(
            setDrawerTranscript({
                channelItem: undefined,
            }),
        );
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
        setTabValue(newValue as TabOptions);
    };

    const recordingUrl = channelItem?.isRecordingDownloaded
        ? `${API_BASE_URL}/contacts/recordings/${channelItem.contactId}/${channelItem.callRecordingId}`
        : '';

    const handleCopyUrlToClipboard = () => {
        setIsCopied(true);
        setTimeout(() => {
            setIsCopied(false);
        }, 3000);
    };

    return (
        <DrawerCommon
            open={!!channelItem}
            handleOnClose={handleClose}
            anchor="right"
            width="800px"
            title="Smart Call Analysis"
            titleNodes={showDevTools && <DevtoolsMenu channelItem={channelItem!} />}
            footer={
                <Stack
                    direction="column"
                    p={2}
                    alignItems="center"
                    justifyContent="center"
                    spacing={2}
                >
                    <PillTabs value={selectedTab} onChange={handleTabChange}>
                        {/* <PillTab value="Details" label="Call Details" /> */}
                        <PillTab value="Summary" label="Call Summary" />
                        <PillTab value="Transcript" label="Transcript" />
                    </PillTabs>
                    {recordingUrl && (
                        <Stack direction="row" spacing={1} width="100%">
                            <audio controls className={classes.audio} ref={audioRef}>
                                <source src={recordingUrl} />
                            </audio>
                            <CopyToClipboard
                                text={recordingUrl || ''}
                                onCopy={handleCopyUrlToClipboard}
                            >
                                <Tooltip
                                    title={isCopied ? 'Copied' : 'Copy Url To Clipboard'}
                                    placement="top"
                                >
                                    <IconButton aria-label="Copy" size="small">
                                        <ShareIcon />
                                    </IconButton>
                                </Tooltip>
                            </CopyToClipboard>
                        </Stack>
                    )}
                    {!recordingUrl && (
                        <Alert severity="info">
                            <Typography variant="caption">
                                *Note: The audio player is only available for calls that have been
                                recorded.
                            </Typography>
                        </Alert>
                    )}
                </Stack>
            }
        >
            <Stack p={1} spacing={2} height="100%">
                {isErrorSummary && <AxiosErrorAlert error={errorSummary} />}
                <PillTabContent tabValue="Details" selectedTab={selectedTab}>
                    <DetailsTab
                        channelItem={channelItem}
                        isImpersonated={isImpersonated}
                        users={users}
                    />
                </PillTabContent>
                <Loader isLoading={isLoadingSummary}>
                    <>
                        <PillTabContent tabValue="Summary" selectedTab={selectedTab}>
                            <SummaryTab
                                dataSummary={dataSummary}
                                contactId={channelItem?.contactId!}
                            />
                        </PillTabContent>
                        <PillTabContent tabValue="Transcript" selectedTab={selectedTab}>
                            <TranscriptTab
                                transcriptSegments={dataSummary?.transcriptSegments}
                                audioRef={audioRef}
                            />
                        </PillTabContent>
                    </>
                </Loader>
            </Stack>
        </DrawerCommon>
    );
}

const useStyles = makeStyles()((theme: Theme) => ({
    audio: {
        display: 'flex',
        flex: 1,
    },
    error: {
        margin: theme.spacing(1),
    },
}));
